import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import { sortByKey, sortByDateKey } from '../../util/sorting'
import { Divider, FlexRow, FlexColumn } from '../styles/Containers'
import { CondensedTable, HeaderRow, HeaderButton, EmptyText, UnfoldMoreIconRight } from '../styles/TableStyles'
import CommunicationRow from './CommunicationRow'

const UserContainer = styled(FlexRow)`
  flex-basis: 12%;
  min-width: 75px;
  align-items: center;
`

const NameContainer = styled.div`
  flex-basis: ${props => (props.$large ? '60%' : '54%')};
`

const DateTimeContainer = styled(FlexRow)`
  flex-basis: ${props => (props.$large ? '40%' : '34%')};
  justify-content: flex-end;
  gap: 5px;
  flex-wrap: wrap;
  word-break: break-word;
`

const ActionSpacer = styled(FlexColumn)`
  width: ${props => (props.$large ? '63px' : '29px')};
  flex-shrink: 0;
`

function getUserName(communication) {
  const user = communication.state === 'DRAFT' ? communication.assignedTo : communication.sentBy
  if (!user) return ''
  return `${user.firstName} ${user.lastName}`
}

function CommunicationTable({ state, communications, disableEditing = false, onEditCommunication = () => {}, handleSelectCommunication = () => {} }) {
  const [sortBy, setSortBy] = useState('date')
  const [sortedCommunications, setSortedCommunications] = useState([])
  const [sortAscending, setSortAscending] = useState(true)

  const isDraft = state === 'DRAFT'

  const SORT_KEYS = {
    user: (a, b) => getUserName(a).localeCompare(getUserName(b)),
    name: sortByKey('name'),
    date: isDraft ? sortByDateKey('updatedAt') : sortByDateKey('sentAt')
  }

  function sort(key) {
    if (key === sortBy) {
      setSortAscending(prev => !prev)
    } else {
      setSortBy(key)
      setSortAscending(true)
    }
  }

  useEffect(() => {
    if (communications && communications.length > 0) {
      const comparator = SORT_KEYS[sortBy]
      let next = [...communications].sort(comparator)
      if (!sortAscending) next = next.reverse()
      setSortedCommunications(next)
    } else {
      setSortedCommunications([])
    }
  }, [communications, sortBy, sortAscending])

  useEffect(() => {
    const availableSortKeys = isDraft ? ['name', 'date'] : ['user', 'name', 'date']
    if (!availableSortKeys.includes(sortBy)) {
      setSortBy('date')
      setSortAscending(true)
    }
  }, [state, sortBy])

  return (
    <CondensedTable>
      <HeaderRow>
        {!isDraft && (
          <UserContainer>
            <HeaderButton variant="text" onClick={() => sort('user')} active={(sortBy === 'user').toString()}>
              Sent By <UnfoldMoreIcon />
            </HeaderButton>
          </UserContainer>
        )}
        <NameContainer $large={isDraft}>
          <HeaderButton variant="text" onClick={() => sort('name')} active={(sortBy === 'name').toString()}>
            Subject <UnfoldMoreIcon />
          </HeaderButton>
        </NameContainer>
        <DateTimeContainer $large={isDraft}>
          <HeaderButton variant="text" onClick={() => sort('date')} active={(sortBy === 'date').toString()}>
            {isDraft ? 'Last Modified' : 'Sent'} <UnfoldMoreIconRight />
          </HeaderButton>
        </DateTimeContainer>
        <ActionSpacer $large={isDraft} />
      </HeaderRow>
      <Divider />
      {sortedCommunications && sortedCommunications.length > 0 ? (
        sortedCommunications.map(communication => {
          return (
            <React.Fragment key={communication.id}>
              <CommunicationRow
                communication={communication}
                disableEditing={disableEditing}
                handleSelectCommunication={handleSelectCommunication}
                onEditCommunication={onEditCommunication}
              />
              <Divider />
            </React.Fragment>
          )
        })
      ) : (
        <EmptyText>No communications available</EmptyText>
      )}
    </CondensedTable>
  )
}

CommunicationTable.propTypes = {
  state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
  communications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
      name: PropTypes.string.isRequired,
      sentAt: PropTypes.instanceOf(Date),
      updatedAt: PropTypes.instanceOf(Date),
      sentBy: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        chipColor: PropTypes.string.isRequired
      })
    })
  ).isRequired,
  disableEditing: PropTypes.bool,
  onEditCommunication: PropTypes.func,
  handleSelectCommunication: PropTypes.func
}

export default CommunicationTable
